import { createFileRoute } from "@tanstack/react-router";
import { products, type Product } from "@/data/products";
import { Breadcrumbs, PageHeader, Section } from "@/components/site/section";
import { CtaLink } from "@/components/site/cta";
import { commonFilters, FilterableGrid } from "@/components/site/product-grid";
import { FinalCTA } from "@/components/site/final-cta";
import { pageUrl } from "@/data/site";

type GallerySearch = {
  status?: string;
  category?: string;
  subcategory?: string;
};

const subcategorySlugMap: Record<string, string> = {
  "fountain-rollerball": "Fountain & Rollerball",
  "bolt-action": "Bolt-Action",
  "rifle-cartridge": "Rifle Cartridge",
  designer: "Designer",
  "sierra-mesa-gatsby": "Sierra Mesa Gatsby",
  "other-ballpoint": "Other Ballpoint",
  "pen-pencil-sets": "Pen & Pencil Sets",
  pencils: "Pencils",
  "cigar-style": "Cigar Style",
  "other-lathe-items": "Other Lathe Items",
  signs: "Signs",
  "fidget-spinners": "Fidget Spinners",
  "3d-carvings": "3D Carvings",
  "other-cnc-items": "Other CNC Items",
  "miscellaneous-items": "Miscellaneous Items",
};

const subcategoryTitleMap: Record<string, string> = {
  "Fountain & Rollerball": "Fountain & Rollerball Pens",
  "Bolt-Action": "Bolt-Action Pens",
  "Rifle Cartridge": "Rifle Cartridge Pens",
  Designer: "Designer Pens",
  "Sierra Mesa Gatsby": "Sierra Mesa Gatsby Pens",
  "Other Ballpoint": "Other Ballpoint Pens",
  "Pen & Pencil Sets": "Pen & Pencil Sets",
  Pencils: "Pencils",
  "Cigar Style": "Cigar Style Pens",
  "Other Lathe Items": "Other Lathe Items",
  Signs: "Signs",
  "Fidget Spinners": "Fidget Spinners",
  "3D Carvings": "3D Carvings",
  "Other CNC Items": "Other CNC Items",
  "Miscellaneous Items": "Miscellaneous Items",
};


export const Route = createFileRoute("/gallery")({
  validateSearch: (search: Record<string, unknown>): GallerySearch => ({
    status: typeof search.status === "string" ? search.status : undefined,
    category: typeof search.category === "string" ? search.category : undefined,
    subcategory: typeof search.subcategory === "string" ? search.subcategory : undefined,
  }),
  head: () => ({
    meta: [
      { title: "All Creations | Handcrafted Gallery | Paul's Pens n Things" },
      {
        name: "description",
        content:
          "Browse every pen, turned gift, carved sign and custom creation Paul has made. Filter by type or availability and request something similar.",
      },
      { property: "og:title", content: "The full gallery of Paul's creations" },
      {
        property: "og:description",
        content: "Every pen, turned gift and carved piece in one place. Use any of them as a starting point.",
      },
      { property: "og:url", content: pageUrl("/gallery") },
    ],
    links: [{ rel: "canonical", href: pageUrl("/gallery") }],
  }),
  component: Gallery,
});

function Gallery() {
  const { status, category, subcategory } = Route.useSearch();
  const subcategoryName = subcategory ? subcategorySlugMap[subcategory] : undefined;

  const galleryFilters = subcategoryName
    ? [
        ...commonFilters,
        {
          id: `subcategory-${subcategory}`,
          label: subcategoryName,
          match: (p: Product) => p.subcategory === subcategoryName,
        },
      ]
    : commonFilters;

  const initialFilter = status === "previously-created" ? "previously-created" : subcategory ? `subcategory-${subcategory}` : undefined;

  return (
    <>
      <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "All Creations" }]} />
      <PageHeader
        eyebrow="The gallery"
        title={subcategoryName ? subcategoryTitleMap[subcategoryName] ?? subcategoryName : "Everything Paul has on record."}
        lede={
          subcategoryName
            ? "Pen styles and materials that are available or can be made to order."
            : "Available pieces, made-to-order styles and work that has already found a home. Nothing here is off-limits as inspiration."
        }
      >
        <CtaLink to="/design-your-piece" arrow className="h-12">
          Design Your Piece
        </CtaLink>
      </PageHeader>

      <Section tone="paper">
        <div className="shell">
          <FilterableGrid
            source={products}
            context="gallery"
            filters={galleryFilters}
            initialFilter={initialFilter}
          />
        </div>
      </Section>

      <FinalCTA />
    </>
  );
}
